import { type Permission, type WorkspaceRole } from '@convo/core';

import { withTabLock } from '../lib/tab-lock';
import { apiGet, apiPost, apiRequest } from './http';

/** 對應 apps/api 的 AuthController 回應。 */

/** 所有分頁的 refresh 排同一條隊。改名等於暫時拆掉跨分頁的保護，新舊版本並存的分頁會各排各的。 */
export const REFRESH_LOCK = 'convo.auth.refresh';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
}

export interface WorkspaceSummary {
  id: string;
  name: string;
  role: WorkspaceRole;
}

export interface SessionResponse {
  user: AuthUser;
  workspace: WorkspaceSummary;
  permissions: Permission[];
  accessToken: string;
}

export interface MeResponse {
  user: AuthUser;
  workspaces: WorkspaceSummary[];
  workspace: WorkspaceSummary | null;
  role: WorkspaceRole;
  permissions: Permission[];
}

export interface RegisterInput {
  email: string;
  password: string;
  name: string;
  workspaceName: string;
  /** 封測邀請碼。從 `/register?code=...` 帶進來，公開註冊關著時是唯一的入口。 */
  inviteCode?: string;
}

export const authApi = {
  register: (input: RegisterInput) => apiPost<SessionResponse>('/auth/register', input),

  /**
   * 忘記密碼。**無論 email 有沒有註冊都會成功**——後端刻意不透露帳號存不存在，
   * 所以前端也不該根據回應分流出兩種畫面。
   */
  forgotPassword: (email: string) =>
    apiPost<{ message: string }>('/auth/forgot-password', { email }),

  /**
   * 確認重設連結還能不能用。過期／用過／不存在都是 404。
   *
   * 404 進 `expectedStatuses`：連結過期是**預期內的答案**，不是壞掉。不標的話使用者
   * 點一條舊連結就會在 console 看到紅字，而真正的錯誤會被這種噪音蓋掉。
   */
  previewReset: (token: string) =>
    apiGet<{ email: string }>(`/auth/reset-password/${token}`, { expectedStatuses: [404] }),

  resetPassword: (token: string, password: string) =>
    apiPost<{ message: string }>('/auth/reset-password', { token, password }),

  login: (email: string, password: string, workspaceId?: string) =>
    apiPost<SessionResponse>('/auth/login', {
      email,
      password,
      ...(workspaceId === undefined ? {} : { workspaceId }),
    }),

  /**
   * 靠 httpOnly cookie，不需要傳任何憑證。
   *
   * 沒有 cookie（400）與 cookie 過期（401）都是正常結果——「還沒登入」「該重新登入了」
   * 不是故障，所以標成預期內，console 不留紅字。
   *
   * **跨分頁要排隊**（E6-04）。refresh token 每用一次就輪替，而 cookie 是所有分頁共用的：
   * 兩個分頁同時拿同一張去換（瀏覽器還原多個分頁、一次開好幾個連結），後端會把晚到的那個
   * 當成「偷來的 token 被重放」，撤銷整條家族——所有分頁一起被登出。排隊之後，後面的分頁
   * 送出時 cookie 已經是前一個剛換到的新 token，兩邊都成功。
   */
  refresh: (workspaceId?: string) =>
    withTabLock(REFRESH_LOCK, () =>
      apiRequest<SessionResponse>('/auth/refresh', {
        method: 'POST',
        body: workspaceId === undefined ? {} : { workspaceId },
        expectedStatuses: [400, 401],
      }),
    ),

  logout: () => apiPost<null>('/auth/logout'),

  me: () => apiGet<MeResponse>('/auth/me'),

  switchWorkspace: (workspaceId: string) =>
    apiPost<Omit<SessionResponse, 'refresh'>>('/auth/switch-workspace', { workspaceId }),
};
